// <Home> — the home screen, redesigned for FIRST-TIME clarity. A newcomer must grasp the game and
// see one obvious action in seconds, so the hero SHOWS the game (word-cards: three players share a
// word, one is different). Humans mostly WATCH now, so the primary CTA is "👀 Watch a live game", then
// "▶ Play now" (take a seat; agents fill the table), then the arena board (live tables · send your agent).
// The old profile-first hero (big "You" avatar) shrinks to a small top-right chip that opens <Me>;
// the Public/Private choice drops to a quiet mini-toggle under the friends button. PURE function of
// `app`; mirrors the classic #home markup (which stays zh in source for the smoke-grep contract).
// In the iOS app (isApp) the home is for players: "▶ Play now" first, then Watch, and today's Daily puzzle (design/IOS-APP.md).
import { html, cx, t, isApp } from '../shared.js';
import { Arena } from './Arena.js';

export function Home({ app }: { app: AppState }) {
  const claimed = Boolean(app && (app as any).acctState && (app as any).acctState.claimed);
  // live tables right now (GET /api/arena, polled while home is up); null until the first answer
  const arena: any = (app as any).arena || null;
  const liveN: number | null = arena && Array.isArray(arena.tables) ? arena.tables.filter((tb: any) => tb.live).length : null;
  // nothing live, but Watch would start a showcase (house agents play for you): say that, not "nothing live"
  const showcase = Boolean(arena && arena.showcase);
  const inApp = isApp();
  const watch = html`
      <button class=${'watch-btn' + (liveN === 0 && !showcase ? ' quiet' : '')} data-action="watch">
        <span class="pn-t">${t('home.watch')}</span>
        <span class="pn-s">${liveN == null ? t('home.watchSub') : liveN ? t('home.watchSubLive', { n: liveN }) : showcase ? t('home.watchSubShowcase') : t('home.watchSubNone')}</span>
      </button>`;
  const play = html`
      <button class="playnow-btn" data-action="play-now">
        <span class="pn-t">${t('home.playNow')}</span>
        <span class="pn-s">${t('home.playNowSub')}</span>
      </button>`;
  return html`
    <div class="home-sky" aria-hidden="true"><span class="hsun"></span></div>
    <div class="home-top">
      <span class="home-logo">🕵️ <b>${t('home.logo')}</b></span>
      <div class="home-topr">
        <button class="home-ico" data-action="leaderboard" title=${t('home.leaderboardTitle')} aria-label=${t('home.leaderboardTitle')}>🏆</button>
        <button class="home-ava" data-action="open-me" aria-label=${t('home.heroAria')}>
          <span class="home-ava-face" id="homeAvatar"></span>
          ${claimed ? '' : html`<span class="home-ava-lock" title=${t('home.lockTitle')} aria-label=${t('home.lockTitle')}>🔓</span>`}
        </button>
      </div>
    </div>

    <div class="home-hero">
      <div class="home-kicker">${t(inApp ? 'home.kickerApp' : 'home.kicker')}</div>
      <h1 class="home-head">${t('home.headLead')}<span class="co">${t('home.headKey')}</span></h1>
      <div class="home-cards" aria-hidden="true">
        <div class="wcard"><span class="wc-em">🍎</span><span class="wc-w">${t('home.wordCommon')}</span></div>
        <div class="wcard"><span class="wc-em">🍎</span><span class="wc-w">${t('home.wordCommon')}</span></div>
        <div class="wcard"><span class="wc-em">🍎</span><span class="wc-w">${t('home.wordCommon')}</span></div>
        <div class="wcard odd"><span class="wc-em">🍐</span><span class="wc-w">${t('home.wordOdd')}</span></div>
      </div>
      <div class="home-cap">${t('home.cardsCap')}</div>
    </div>

    <div class="home-actions">
      ${inApp ? [play, watch] : [watch, play]}
      <div class="subrow">
        <button class="friends-btn ghost" data-action="create"><span class="sr-t">${t('home.withFriends')}</span><span class="sr-s">${t('home.withFriendsSub')}</span></button>
        <button class="hall-btn ghost" data-action="browse"><span class="sr-t">${t('home.browse')}</span><span class="sr-s">${t('home.joinSub')}</span></button>
      </div>
      ${inApp ? html`<div class="subrow"><button class="daily-btn ghost" data-action="daily"><span class="sr-t">${t('home.daily')}</span><span class="sr-s">${t('home.dailySub')}</span></button></div>` : ''}
    </div>

    <div id="arena"><${Arena} app=${app} /></div>
    <div class="home-links"><span data-action="rules">${t('home.rules')}</span></div>
    <div class="home-legal"><a href="/privacy">${t('home.privacyLink')}</a><span class="sep">·</span><a href="/terms">${t('home.termsLink')}</a><span class="sep">·</span><a href="/support">${t('home.supportLink')}</a><span class="sep">·</span><span class="copy">${t('home.copyright')}</span></div>
  `;
}
